import { FunctionComponent as F, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { LISTINGS, PRODUCTS, PROTOCOL } from "../../core/router/paths";
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import SavingsTable from "./SavingsTable";
import { IState } from "../../core/store/store";
import { updateProducts } from "../../core/store/products/products.slice";
import IndividualCostBreakdown from "./IndividualCostBreakdown";
import RowsCostBreakdown from "./RowsCostBreakdown";
import BundleCostBreakdown from "./BundleCostBreakdown";
import { productDesc } from "../../utils/string";
import { DosingCosts, ListingInfo, IProductInfo, DosingRowWithSavings } from "@protoplan/types";


interface LocationState
{
  protocolRow: DosingRowWithSavings & ListingInfo & IProductInfo & DosingCosts;
}

interface IParams
{
  protocolId?: string
}

const Savings: F = () =>
{
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams() as IParams;
  const state = location.state as LocationState | undefined;

  const storedProductId = useSelector((state: IState) => state.listings.productId);

  const protocolRow = state?.protocolRow;

  const [selectedListingSavingsId, setSelectedListingSavingsId] = useState(protocolRow?.listingSavings?.length ?
    protocolRow.listingSavings[0].savingId :
    undefined);
  const [selectedBundleSavingsId, setSelectedBundleSavingsId] = useState(protocolRow?.bundleSavings?.length ?
    protocolRow.bundleSavings[0].savingId :
    undefined);

  if(!protocolRow || (!protocolRow.listingSavings?.length && !protocolRow.bundleSavings?.length))
  {
    navigate(PROTOCOL + "/" + params.protocolId);
    return <></>;
  }

  const selectedListingSavings = protocolRow.listingSavings
    ?.filter(s => s.savingId === selectedListingSavingsId);
  const selectedListingSavingRow = selectedListingSavings?.length ? selectedListingSavings[0] : undefined;

  const selectedBundleSavings = protocolRow.bundleSavings
    ?.filter(s => s.savingId === selectedBundleSavingsId);
  const selectedBundleSavingRow = selectedBundleSavings?.length ? selectedBundleSavings[0] : undefined;


  const handleSeeAllListings = () =>
  {
    dispatch(updateProducts([protocolRow]));
    navigate("../" + PRODUCTS + "/" + protocolRow.productId + "/" + LISTINGS, { relative: 'path', state: {
      retrieveListings: protocolRow.productId !== storedProductId,
      swapDosingId: protocolRow.dosingId } });
  };


  return (
    <Dialog
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { p: '15px' }}}
      open={true}
      onClose={() => navigate(PROTOCOL + "/" + params.protocolId)}>
      <DialogContent>
        {(protocolRow.listingSavings?.length && selectedListingSavingRow) ?
          <>
            <Typography style={{ fontSize: 30, fontWeight: 600, marginBottom: 24 }}>
              {"Individual Product Savings"}
            </Typography>
            <Stack spacing={6} sx={{ width: '100%' }}>
              <IndividualCostBreakdown row={protocolRow} savingType={"current row"}/>
              <IndividualCostBreakdown
                row={selectedListingSavingRow}
                savingType={selectedListingSavingRow.savingId === protocolRow.listingSavings[0].savingId ?
                  "best saving" :
                  "selected saving"}/>
              <Stack spacing={1.5} sx={{ width: '100%' }}>
                <Typography variant='body2' style = {{ fontSize: 14, fontWeight: 600 }}>
                  {"All individual product savings on " + protocolRow.productName + ":"}
                </Typography>
                <SavingsTable
                  protocolId={Number(params.protocolId)}
                  savings={protocolRow.listingSavings}
                  swapDosingId={protocolRow.dosingId}
                  selected={selectedListingSavingsId}
                  handleToggleSelected={(savingId: number) => setSelectedListingSavingsId(savingId)}/>
              </Stack>
            </Stack>
            <Box sx={{ height: 50 }}/>
          </> :
          <></>}

        {(protocolRow.bundleSavings?.length && selectedBundleSavingRow) ?
          <>
            <Typography style={{ fontSize: 30, fontWeight: 600, marginBottom: 24 }}>
              {"Bundle Savings"}
            </Typography>

            <Stack spacing={6} sx={{ width: '100%' }}>
              <RowsCostBreakdown rows={selectedBundleSavingRow.replaceableRows} savingType={"current rows"}/>
              <BundleCostBreakdown
                protocolRow={protocolRow}
                bundleSaving={selectedBundleSavingRow}
                savingType={"best bundle saving"}/>
              <Stack spacing={1.5} sx={{ width: '100%' }}>
                <Typography variant='body2' style = {{ fontSize: 14, fontWeight: 600 }}>
                  {"All bundle savings including " + productDesc(protocolRow) + ":"}
                </Typography>
                <SavingsTable
                  protocolId={Number(params.protocolId)}
                  savings={protocolRow.bundleSavings.map(bs => ({
                    ...bs,
                    ...bs.replaceableRows[0],
                    ...bs.bundle[0],
                    saving: bs.bundleSaving })) || []}
                  swapDosingId={protocolRow.dosingId}
                  selected={selectedBundleSavingsId}
                  handleToggleSelected={(savingId: number) => setSelectedBundleSavingsId(savingId)}/>
              </Stack>
            </Stack>
            <Box sx={{ height: 50 }}/>
          </> :
          <></>}

        <Stack spacing={1.5} direction={'row'} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1 }}/>
          <Link
            underline='none'
            variant='body2'
            style={{ fontSize: 14, cursor: 'pointer' }}
            onClick={handleSeeAllListings}>
            {"See all listings"}
          </Link>
          <ArrowForwardIos sx={{ color: 'lightGray', cursor: 'pointer' }} onClick={handleSeeAllListings}/>
        </Stack>
      </DialogContent>
    </Dialog>);
};

export default Savings;
